import { d3, cropr, gettext } from "../adapter";
import each from "lodash/each";
import extend from "lodash/extend";
import moment from "moment";
import timeFormat from "./chartTimeFormat";
import weatherStationActions from "./../actions/weatherStationActions";

// The constructor
class Chart {
  constructor(options, data, dispatch) {
    // Empty data, this is our data format
    this.dispatch = dispatch;
    this.data = { values: [] };
    this.precipitationInterval = 0;
    this.stationId = false;
    this.fetchPeriod = [];
    this.hasAT = false;
    var _this = this;

    // Default options
    this.options = {
      id: false,
      class: false,
      width: 500,
      height: 350,
      isBrush: true,
      i18n: {
        unit: cropr.l10n.unit_temp_display, //I18n.t('units.degrees.short'),
        unitBig: cropr.l10n.unit_temp_display, //I18n.t('units.degrees.long'),
      },
    };

    // Merge the options
    this.options = extend(this.options, options);

    // Set chart dimension, also depending on settings as brush
    var margin = { top: 10, right: 70, bottom: 30, left: 70 };
    var height = this.options.height - (margin.top + margin.bottom);
    var width = this.options.width - (margin.left + margin.right);

    this.chart = {
      width: width,
      height: height,
      margin: margin,
    };

    this.totalChart = {
      height: this.chart.height,
      margin: {
        top: this.chart.margin.top,
      },
    };

    // Set the data
    if (data) {
      this.setData(data);
    }

    var scrolling;

    this.zoom = d3.behavior
      .zoom()
      .x(this.scales.x)
      .on("zoom", function () {
        if (_this.scales.x.domain()[0] < _this.data.minDate) {
          var x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.minDate) +
            _this.scales.x.range()[0];
          _this.zoom.translate([x, 0]);
        } else if (_this.scales.x.domain()[1] > _this.data.maxDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.maxDate) +
            _this.scales.x.range()[1];
          _this.zoom.translate([x, 0]);
        }

        // Execute only once
        if (!scrolling) {
          _this.elements.eventRect.classed("grabbing", true);
          _this.dispatch(weatherStationActions.hovered(false));
        }

        scrolling = true;
        _this._scroll();
      })
      .on("zoomend", function () {
        if (!scrolling) return;

        _this.elements.eventRect.classed("grabbing", false);

        // Enable the pin hovers again when we stop zooming
        if (this === d3.event.sourceEvent.target) {
          _this.dispatch(weatherStationActions.hovered(true));
        }

        scrolling = false;
        _this.dispatch(
          weatherStationActions.brushDomain([
            _this.scales.x.domain()[0],
            _this.scales.x.domain()[1],
          ])
        );
      });

    // No SVG and elements yet
    this.elements = {};
  }
}

// Init graph
Chart.prototype.init = function (el) {
  // Only init once and only init if we have data
  if (this.svg || !this.data) {
    return false;
  }

  // Create the SVG
  this.elements.svg = d3
    .select(el)
    .append("svg")
    .attr("class", this.options.class)
    .attr("id", this.options.id)
    .attr("width", this.options.width)
    .attr("height", this.options.height);

  var chart = (this.elements.chart = this.elements.svg
    .append("g")
    .attr("class", "chart")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")"));

  this.elements.innerChart = this.elements.svg
    .append("g")
    .attr("class", "inner")
    .attr("clip-path", "url(#clip-" + this.options.id + ")")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

  this.elements.grid = {
    x: chart
      .append("g")
      .attr("class", "grid x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "grid y"),
  };

  this.elements.legendList = d3
    .select(el)
    .append("ul")
    .attr("class", "l-stacked legend has-left-spacing");
  this.elements.legendLine = this.elements.legendList.append("li").attr("class", "legend first");
  this.elements.legendLine.append("span").attr("class", "airTemperature line").html("&nbsp;");
  this.elements.legendLine
    .append("span")
    .attr("class", "content")
    .html(
      gettext("Air temperature weather in {{unit}}").replace("{{unit}}", this.options.i18n.unitBig)
    );

  this.elements.legendPrecipitation = this.elements.legendList.append("li").attr("class", "legend");
  var precipitationSpan = this.elements.legendPrecipitation
    .append("span")
    .attr("class", "precipitation");
  precipitationSpan.append("span").attr("class", "precipitation1");
  precipitationSpan.append("span").attr("class", "precipitation2");
  precipitationSpan.append("span").attr("class", "precipitation3");

  this.elements.legendSpan = this.elements.legendPrecipitation
    .append("span")
    .attr("class", "content")
    .html(
      gettext("Precipitation in {{unit}} per {{interval}} hour")
        .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
        .replace("{{interval}}", this.precipitationInterval)
    );

  var brushSpacing = 15;

  this.elements.chartCover = this.elements.svg
    .append("g")
    .attr("class", "cover")

    // .attr('clip-path', 'url(#clip-' + this.options.id + ')')
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    );

  // Draw the mouse vertical line
  var clipId = "clip";

  // Draw the mouse vertical line
  this.elements.mouse = this.elements.chartCover
    .append("g")
    .attr("clip-path", "url(#" + clipId + "-" + this.options.id + ")")
    .append("g")
    .attr("class", "mouse")
    .style("opacity", "0");

  this.elements.mouse
    .append("line")
    .attr("class", "line mouse")
    .attr("x1", 2)
    .attr("y1", 8 + brushSpacing)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  this.elements.mouse
    .append("circle")
    .attr("class", "circle mouse")
    .attr("cx", 2)
    .attr("cy", 0 + brushSpacing)
    .attr("r", 6);

  // Draw the axis
  this.elements.axis = {
    x: chart
      .append("g")
      .attr("class", "axis x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "axis y"),
    yPrecipitation: chart
      .append("g")
      .attr("class", "axis yPrecipitation")
      .attr("transform", "translate(" + (this.chart.width - 1) + ", 0)"),
  };

  this.elements.brush = this.elements.svg
    .append("g")
    .attr("class", "brush")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

  // Add the brush
  this._addBrush();

  // Draw the clippaths
  this._drawClipPaths();

  this.update();
};

// Set the data we need, we expect to receive it in the correct format
Chart.prototype.setData = function (data) {
  // Set the data
  this.data = extend(this.data, data);

  // Set min and max data value
  this.data.minDate = this.data.station.firstDataPointDate;
  this.data.maxDate = this.data.station.lastDataPointDate;
  this.hasAT = this.data.station.measurements.raw[0].values.air_temperature !== undefined;

  // Create or update scales and axis etc.
  this._setScales();
  this._setAxis(); // @TODO: Do we need to update the axis after we reload data or is just the scales function enough?

  this._setDataShapes(); // @TODO: This propbably doesn't need to happen at every update
};

Chart.prototype.updateHoverState = function (hovered, pinned, pinDate) {
  if (!pinned) {
    this.elements.mouse.style("opacity", "0");
  } else {
    this.data.pinDate = pinDate;
    this.elements.mouse.style("opacity", "1");
    // Only for the brush
    this.updateHoverDate(pinDate, pinned);
    this.elements.mouse.attr(
      "transform",
      "translate(" + (this.scales.x(pinDate) - 2) + "," + 0 + ")"
    );
  }

  this.data.pinned = pinned;
};

Chart.prototype.updateHoverDate = function (date, pinned) {
  if (!pinned) {
    this.elements.mouse.attr("transform", "translate(" + (this.scales.x(date) - 2) + "," + 0 + ")");
  }
};

// Update
Chart.prototype.update = function (data) {
  var _this = this;
  var brushFunction = this.brushFunction;

  if (data) {
    // DIE SETDATA FUNCTIE BETER BEKIJKEN
    this.setData(data);
    this.zoom.x(this.scales.x);
    var redraw = false;

    if (this.fetchPeriod !== this.data.fetchPeriod) {
      _this.stationId = false;
      _this.data.domain = [
        new Date(_this.data.fetchPeriod[0] * 1000),
        new Date(_this.data.fetchPeriod[1] * 1000),
      ];
    }

    // Only adapt the viewfinder if the domain is smaller
    if (!(this.data.domain[0] <= this.data.minDate && this.data.domain[1] >= this.data.maxDate)) {
      this.brushFunction.extent(this.data.domain);
      this.viewFinder.transition().call(brushFunction);
    }

    this.scales.x.domain(
      d3.extent(
        _this.data.station.measurements.airTemperatureBrush.map(function (d) {
          return d.timestamp;
        })
      )
    );
  }

  this.elements.legendLine.style("opacity", this.hasAT ? 1 : 0);
  this.elements.axis.y.style("opacity", this.hasAT ? 1 : 0);

  if (this.data.station.id !== this.stationId) {
    redraw = true;
    this.fetchPeriod = this.data.fetchPeriod;
    this.stationId = this.data.station.id;
    this.brushFunction.x(_this.scales.x).extent(_this.data.domain);
    this.viewFinder.transition().call(brushFunction);
  }

  //  var compressedData = this._compressBatched(this.data.station.measurements.precipitation, redraw);
  if (
    this.data.station.showAllMeasurements === true ||
    this.data.station.showFiveMinutesMeasurements === true ||
    this.data.station.measurements.precipitation.length < 200
  ) {
    var compressedData = this.data.station.measurements.precipitation;
    this.data.compressedData = compressedData;
    this.precipitationInterval = 1;
  } else {
    compressedData = this._compressBatched(this.data.station.measurements.precipitation, redraw);
  }

  var maxYPrecipitationDomain = d3.max(
    compressedData.map(function (d) {
      return d.y;
    })
  );

  if (!maxYPrecipitationDomain) maxYPrecipitationDomain = 5;
  this.scales.yPrecipitation.domain([0, maxYPrecipitationDomain]);

  var airTemperature = this.data.station.measurements.airTemperatureBrush;

  this.elements.axis.x.transition().call(this.axis.x);
  this.elements.axis.y.transition().call(this.axis.y);
  this.elements.grid.x.transition().call(this.axis.gridX);
  this.elements.grid.y.transition().call(this.axis.gridY);
  this.elements.axis.yPrecipitation.transition().call(this.axis.yPrecipitation);
  this.elements.axis.yPrecipitation.attr(
    "transform",
    "translate(" + (this.chart.width - 1) + ", 0)"
  );

  if (!this.options.isBrush && this.data.pinned) {
    this.elements.mouse
      .transition()
      .attr("transform", "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")");
  }

  var selection = this.elements.innerChart.selectAll(".bar.stack").data(compressedData);
  selection.enter().append("rect").attr("class", "precipitation bar stack");
  selection.exit().remove();

  selection
    .transition()
    .attr("x", function (d) {
      return _this.scales.x(d.timestamp);
    })
    .attr("y", function (d) {
      return _this.scales.yPrecipitation(d.y);
    })
    .attr("height", function (d) {
      return _this.chart.height - _this.scales.yPrecipitation(d.y);
    })
    .attr("width", 3);

  // Update the line graph data
  if (!this.elements.line) {
    this.elements.line = this.elements.innerChart
      .append("path")
      .attr("class", "line airTemperature");
  }

  if (this.hasAT) {
    // Draw the temp line
    this.elements.line.style("opacity", 1);
    this.elements.line.datum(airTemperature);
    this.elements.line.transition().attr("d", this.shapes.line);
  } else {
    this.elements.line.style("opacity", 0);
  }
  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  this.elements.legendSpan.html(
    gettext("Precipitation in {{unit}} per {{interval}} hour")
      .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
      .replace("{{interval}}", this.precipitationInterval)
  );

  if (this.options.isBrush) {
    this.brushFunction.x(this.scales.x).extent(this.data.domain);
    this.viewFinder.call(brushFunction);
  }
};

Chart.prototype.resize = function (width, height) {
  var brushFunction = this.brushFunction;
  if (width) {
    this.options.width = width;
    this.chart.width = this.options.width - (this.chart.margin.left + this.chart.margin.right);
  }

  if (height) {
    this.options.height = height;
    this.chart.width = this.options.height - (this.chart.margin.top + this.chart.margin.bottom);
  }

  this.elements.svg.style("width", this.options.width);
  this.elements.svg.style("height", this.options.height);

  // Update the scales
  this._setScales();
  this._setAxis();
  this.update();

  if (this.options.isBrush) {
    this.brushFunction.x(this.scales.x).extent(this.data.domain);
    this.viewFinder.call(brushFunction);
  }
};

Chart.prototype.destroy = function () {
  this.elements.svg.remove();
};

Chart.prototype._setScales = function () {
  var domain = this.data.domain;

  domain = d3.extent(
    this.data.station.measurements.airTemperatureBrush.map(function (d) {
      return d.timestamp;
    })
  );

  var maxYDomain = d3.max(
    this.data.station.measurements.airTemperatureBrush.map(function (d) {
      return d.y;
    })
  );

  var minYDomain = d3.min(
    this.data.station.measurements.airTemperatureBrush.map(function (d) {
      return d.y;
    })
  );

  if (!maxYDomain) maxYDomain = 10;
  if (!minYDomain) minYDomain = 0;

  this.scales = {
    x: d3.time.scale().range([0, this.chart.width]).domain(domain),
    y: d3.scale.linear().range([this.chart.height, 0]).domain([minYDomain, maxYDomain]),
    yPrecipitation: d3.scale.linear().range([this.chart.height, 0]),
  };
};

Chart.prototype._setAxis = function () {
  var _this = this;
  var precision = cropr.l10n.unit_precipitation === "in" ? 2 : 1;

  // Calculate the amount of ticks
  var ticksY = Math.round(this.chart.height / 40);
  var ticksX = Math.round(this.chart.width / 150);

  this.axis = {
    x: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat(timeFormat.chart),

    y: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return d + " " + _this.options.i18n.unit;
      }),

    yPrecipitation: d3.svg
      .axis()
      .scale(this.scales.yPrecipitation)
      .orient("right")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return cropr.toLocaleFixed(d, precision) + " " + cropr.l10n.unit_precipitation_display;
      }),

    gridX: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.chart.height, 0, 0)
      .tickFormat(""),
    gridY: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY * 2)
      .tickSize(-this.chart.width, 0, 0)
      .tickFormat(""),
  };
};

Chart.prototype._setDataShapes = function () {
  var _this = this;

  this.shapes = {
    line: d3.svg
      .line()
      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y(function (d) {
        let y = _this.scales.y(d.y);
        return y;
      }),
  };
};

Chart.prototype._addBrush = function () {
  var _this = this;
  var tooltipMarginLeftRight = 5; // 5px margin
  var tooltipMarginTopBottom = 4; // 5px margin
  var viewFinder;

  // Brush function
  var brushFunction;
  this.brushFunction = brushFunction = d3.svg
    .brush()
    .x(this.scales.x)
    .extent(this.data.domain)
    .on("brushend", _brushend)
    .on("brushstart", _brushStart)
    .on("brush", _brushing);

  this.viewFinder = viewFinder = this.elements.brush
    .append("g")
    .attr("class", "x brush")
    .call(brushFunction);

  // Paint the view finder
  var draggerSize = 24; // The width and height of the dragger

  viewFinder
    .selectAll("rect")
    .attr("y", -1)
    .attr("height", this.chart.height + 2);

  viewFinder
    .selectAll(".resize.e")
    .append("rect")
    .attr("transform", "translate(-1," + (this.chart.height / 2 - draggerSize / 2) + ")")
    .attr("class", "dragger")
    .attr("width", draggerSize - 1)
    .attr("height", draggerSize)
    .attr("rx", 2)
    .attr("ry", 2);

  viewFinder
    .selectAll(".resize.w")
    .append("rect")
    .attr(
      "transform",
      "translate(-" + (draggerSize - 2) + "," + (this.chart.height / 2 - draggerSize / 2) + ")"
    )
    .attr("class", "dragger")
    .attr("width", draggerSize - 1)
    .attr("height", draggerSize)
    .attr("rx", 2)
    .attr("ry", 2);

  // Add the viewfinder data
  var tooltip = viewFinder.append("g").attr("class", "tooltip is-hidden");

  var tooltipText = tooltip
    .append("text")
    .attr("class", "tooltipText")
    .attr("text-anchor", "middle");

  var tooltipBalloon = tooltip.insert("rect", "text.tooltipText").attr("rx", 2).attr("ry", 2);

  // Set the tooltip of the brush
  //setBrushTooltip();

  var draggerWest = viewFinder
    .selectAll(".resize.w")
    .append("g")
    .attr("class", "draggerlines")
    .attr(
      "transform",
      "translate(-" + (draggerSize + 1) + "," + (this.chart.height / 2 - draggerSize / 2) + ")"
    );
  draggerWest
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 9)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerWest
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 13)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerWest
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 17)
    .attr("width", 2)
    .attr("height", draggerSize - 13);

  var draggerEast = viewFinder
    .selectAll(".resize.e")
    .append("g")
    .attr("class", "draggerlines")
    .attr("transform", "translate(-1," + (this.chart.height / 2 - draggerSize / 2) + ")");
  draggerEast
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerEast
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 11)
    .attr("width", 2)
    .attr("height", draggerSize - 13);
  draggerEast
    .append("rect")
    .attr("y", 7)
    .attr("rx", 1)
    .attr("x", 15)
    .attr("width", 2)
    .attr("height", draggerSize - 13);

  function _brushend() {
    var brush = brushFunction.empty() ? _this.scales.x.domain() : brushFunction.extent();
    tooltip.attr("class", "tooltip is-hidden");

    // Apply brush action
    _this.dispatch(weatherStationActions.brushDomain(brush));
  }

  function _brushStart() {
    tooltip.attr("class", "dacom-online-tooltip");
    setBrushTooltip();
  }

  function _brushing() {
    setBrushTooltip();

    //@TODO add date to brush
  }

  function setBrushTooltip() {
    var brush = brushFunction.empty() ? _this.scales.x.domain() : brushFunction.extent();

    var brushStartPosition = _this.scales.x(brush[0]);
    var brushStopPosition = _this.scales.x(brush[1]);

    var tooltipCenter = brushStartPosition + (brushStopPosition - brushStartPosition) / 2;

    var dateText = moment(brush[0]).format("dd, LL") + " — " + moment(brush[1]).format("dd, LL");
    dateText = dateText.toUpperCase();
    tooltipText.text(dateText);

    // Calculate the border box
    var bbox;
    bbox = tooltipText[0][0].getBBox();
    var bboxWidth = bbox.width + 2 * tooltipMarginLeftRight;
    tooltipBalloon
      .attr("x", bbox.x - tooltipMarginLeftRight)
      .attr("y", bbox.y - tooltipMarginTopBottom)
      .attr("width", bboxWidth)
      .attr("height", bbox.height + 2 * tooltipMarginTopBottom);

    var brushWidth = _this.elements.innerChart[0][0].getBBox().width;

    tooltipCenter = Math.max(bboxWidth / 2, tooltipCenter);
    tooltipCenter = Math.min(brushWidth - bboxWidth / 2, tooltipCenter);
    tooltip.attr("transform", "translate(" + tooltipCenter + "," + (_this.chart.height + 22) + ")");
  }
};

Chart.prototype._drawClipPaths = function () {
  var defs;
  this.defs = defs = this.elements.svg.append("defs");

  this.defs.clip = defs
    .append("svg:clipPath")
    .attr("id", "clip-" + this.options.id)
    .append("svg:rect")
    .attr("x", 0)
    .attr("y", -8)
    .attr("height", this.chart.height + 8);

  this.defs.totalClip = defs
    .append("svg:clipPath")
    .attr("id", "clip-total-" + this.options.id)
    .append("svg:rect")
    .attr("x", "0")
    .attr("y", "-8")
    .attr("height", this.totalChart.height + 8);

  this.defs.mask = defs
    .append("svg:mask")
    .attr("id", "mask-" + this.options.id)
    .attr("width", this.chart.width)
    .attr("height", this.chart.height + 8)
    .attr("x", 0)
    .attr("y", -8);
};

Chart.prototype._compressBatched = function (data, redraw) {
  // Interval in hours
  // To be calulated
  var interval = 1; // Data is per kwartier, 4 intervals is een uur
  var barWidth = 3;

  // How many bars are going to fit insite the current chart
  var fittingBars = this.chart.width / barWidth;
  var domain = d3.extent(
    this.data.station.measurements.airTemperatureBrush.map(function (d) {
      return d.timestamp;
    })
  );

  // How many hours is our current domain
  var domainStart = moment(domain[0]);
  var domainEnd = moment(domain[1]);

  var diff = domainEnd.diff(domainStart, "hours");

  while (fittingBars * interval < diff) {
    interval++;
  }

  if (interval > 12) {
    interval = 24;
  } else if (interval > 6) {
    interval = 12;
  } else if (interval > 4) {
    interval = 6;
  }

  this.precipitationInterval = interval;

  var compressedData = [];
  var sum = 0;
  var maxTime = false;

  each(data, function (value) {
    var time = value.timestamp;
    if (!maxTime) {
      maxTime = new Date(time);
      var correction = time.getHours() % interval;
      maxTime.setMinutes(0);
      if (interval === 1) {
        maxTime.setTime(maxTime.getTime());
      } else {
        maxTime.setTime(maxTime.getTime() + (interval - correction) * 3600 * 1000);
      }
    }

    if (maxTime <= time) {
      // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
      var averageTime = new Date(maxTime);
      averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

      compressedData.push({
        timestamp: averageTime,
        y: sum,
      });

      sum = 0;

      maxTime = false;
    }

    // @TODO: before adding the value to the sum, check if the measurement is active
    sum += value.y;
  });

  // The last push, with the remaining rain fall
  if (maxTime) {
    // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
    var averageTime = new Date(maxTime);
    averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

    compressedData.push({
      timestamp: averageTime,
      y: sum,
    });
  }

  this.data.compressedData = compressedData;
  return compressedData;
};

export default Chart;
